import {
  ChartConfig,
  ChartId,
  DashboardChart,
  ResolvedRoleLevels,
  SaveChartResponse
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Role } from '@blisspointmedia/bpm-types/dist/Users';
import { awaitJSON, DashboardingLambdaFetch } from '../../utils/fetch-utils';

// Options that ride alongside the config on the wire. An OBJECT rather than more positional
// arguments: all existing call sites pass exactly two arguments and so need no change, and no caller
// has to restate `render` just to set a role level.
interface SaveChartOptions {
  render?: boolean;
  // The two governance levels. Send them ONLY where the levels are the point — the sharing control
  // and the copy path. Every ordinary save leaves them out, and the backend then keeps whatever is
  // stored. Restating them on a layout save would let one user's drag undo another user's audience
  // change.
  viewRoleRequired?: number;
  editRoleRequired?: number;
}

// Save one ChartConfig and return the saved chart, as the server stored it. Shared by the agent's
// dashboard-build path and the single-chart view's explicit exits (Update / Save as a new chart).
// `render` defaults to false: a promotion only persists, since the preview already compiled + ran the
// config via /getData. Passing a config WITHOUT an `id` (Save as a new chart) makes the backend mint a
// fresh chart:<uuid> instead of upserting in place; a config WITH its id upserts (Update everywhere
// it's used).
//
// Returns the whole envelope, not just the id, so a caller can write SERVER TRUTH into the query
// cache. The server sets `modifiedAt` and `lastModifiedBy`, and it clamps both role levels — so an
// envelope rebuilt on the client can disagree with the stored row, and this one cannot.
//
// The two levels are siblings of `config` on the wire, never fields inside it. That is the shape
// `SaveChartParams` declares, and getting it wrong fails silently: the backend simply reads no levels
// and keeps the stored ones.
export const saveChart = async (
  company: string,
  config: Omit<ChartConfig, 'id'> & { id?: ChartId },
  opts: SaveChartOptions = {}
): Promise<DashboardChart> => {
  const res = await DashboardingLambdaFetch('/saveChart', {
    method: 'POST',
    body: { company, config, render: false, ...opts }
  });
  const { chart } = await awaitJSON<SaveChartResponse>(res);
  return chart;
};

/**
 * The pair a BRAND-NEW record is born with, stated once on the client.
 *
 * ⚠️ IT MUST MATCH THE SERVER'S, which is `resolveRoleThresholds` in bpm-lambdas
 * (`dashboarding/permissions.ts`): view defaults to `Role.EXTERNAL`, edit to
 * `Math.max(callerRole, Role.INTERNAL)`. Every caller here needs the pair the backend WOULD have
 * chosen, because they send both levels explicitly — a chat-requested audience names at most one of
 * the two, and the ceiling cannot be applied to one level without the other.
 *
 * The floor is not decoration. The backend applies it ONLY to an edit level it defaults itself; a
 * level the client SENDS goes through `clampToCallerRole` (`Math.max(requested, callerRole)`), which
 * is a no-op when the two are equal. So sending the caller's bare role bypasses the floor: an
 * administrator's new chart would land at edit level 0, and the role-1 colleagues they were working
 * with would fail the edit check on a chart they had just been handed.
 *
 * View stays widest so a new record is never born invisible to the client it was made for; a user
 * narrows an audience on purpose, never as a side effect of making something.
 */
export const createDefaultRoleLevels = (userRole: number): ResolvedRoleLevels => ({
  viewRoleRequired: Role.EXTERNAL,
  editRoleRequired: Math.max(userRole, Role.INTERNAL)
});

/**
 * The role levels a COPY carries: inherit the original's audience, own the edit rights.
 *
 * The copy keeps the original's VIEW level, so it stays visible to exactly the people who could see
 * what it was copied from, and takes the copier's own role as its EDIT level, so they can work on it.
 *
 * The edit half comes from `createDefaultRoleLevels` above, so a copy and a brand-new chart are born
 * with the same edit level by construction rather than by two matching expressions.
 *
 * `undefined` when there is no original to inherit from — a draft's first save. The backend then
 * applies its create defaults (view 10, edit `max(role, 1)`) rather than a pair assembled from a row
 * that does not exist. Callers holding a SAVED chart must not reach this with `undefined`: that would
 * silently publish the copy at the server's widest default view level instead of the original's
 * audience, so they check for the envelope first and refuse rather than widen.
 */
export const copyRoleLevels = (
  original: ResolvedRoleLevels | undefined,
  userRole: number
): SaveChartOptions | undefined =>
  original && {
    viewRoleRequired: original.viewRoleRequired,
    editRoleRequired: createDefaultRoleLevels(userRole).editRoleRequired
  };
